import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { resetCookieConsent } from "./CookieConsent";
import {
  LayoutDashboard,
  Store,
  ListChecks,
  ClipboardList,
  ClipboardCheck,
  AlertTriangle,
  UserCheck,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Languages,
  Share2,
  Users as UsersIcon,
  ShieldCheck,
  Gamepad2,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Switch } from "./ui/switch";
import AiHelpAssistant from "./AiHelpAssistant";
import NotificationBell from "./NotificationBell";

const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
  { to: "/assignments", key: "assignments", icon: ClipboardCheck, perm: "view_assignments" },
  { to: "/stores", key: "stores", icon: Store, perm: "view_stores" },
  { to: "/checklist", key: "checklist", icon: ListChecks, perm: "view_checklist" },
  { to: "/audits", key: "audits", icon: ClipboardList, perm: "view_audits" },
  { to: "/findings", key: "findings", icon: AlertTriangle, perm: "view_findings" },
  { to: "/follow-up", key: "followup", icon: UserCheck, perm: "view_followup" },
  { to: "/games", key: "games", icon: Gamepad2, perm: "view_dashboard" },
  { to: "/users", key: "users", icon: UsersIcon, perm: "view_users" },
  { to: "/permissions", key: "permissions", icon: ShieldCheck, perm: "manage_permissions" },
];

export default function Layout() {
  const { user, logout, activeStore, viewAllStores, setViewAllStores, can } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const visibleNav = NAV.filter((n) => !user || can(n.perm));
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const toggleLang = () => {
    const next = i18n.language?.startsWith("en") ? "es" : "en";
    i18n.changeLanguage(next);
  };

  const shareApp = async () => {
    const url = window.location.origin;
    const data = {
      title: `${t("brand")} · ${t("app_name")}`,
      text: t("share_app_msg"),
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success(t("share_app_copied"));
      }
    } catch (e) {
      // user cancelled — silent
    }
  };

  const isAuditor = user?.role === "auditor";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-[1500px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-3 shrink-0"
            data-testid="brand-home"
            aria-label="Mueblería Canales"
          >
            <img
              src="/canales-logo-mark.svg"
              alt="Canales Furniture"
              className="h-11 w-auto"
              draggable="false"
            />
            <div className="hidden md:block leading-tight">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Operations</div>
              <div className="font-display text-sm">{t("app_name")}</div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1 ml-3 flex-1" data-testid="topnav">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `topnav-link ${isActive ? "is-active" : ""}`}
                data-testid={`nav-${n.key}`}
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1 lg:hidden" />

          {/* Auditor: View all stores toggle */}
          {isAuditor && user?.assigned_store_id && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200" data-testid="view-all-toggle-wrap">
              {viewAllStores ? <Eye size={14} className="text-[#1E3A8A]" /> : <EyeOff size={14} className="text-stone-500" />}
              <span className="text-xs font-medium text-stone-700">{t("common.view_all_stores")}</span>
              <Switch
                checked={viewAllStores}
                onCheckedChange={setViewAllStores}
                data-testid="view-all-toggle"
              />
            </div>
          )}

          {activeStore && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E5EBFA] text-[#1E3A8A] text-xs font-semibold">
              <Store size={14} /> {activeStore.tienda}
            </div>
          )}

          {/* Share app */}
          <button
            onClick={shareApp}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-stone-100 text-xs font-semibold"
            data-testid="share-app-button"
            title={t("common.share_app")}
          >
            <Share2 size={14} /> <span className="hidden md:inline">{t("common.share_app")}</span>
          </button>

          {/* Language */}
          <button
            onClick={toggleLang}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full hover:bg-stone-100 text-xs font-bold"
            data-testid="lang-toggle"
          >
            <Languages size={13} /> {i18n.language?.startsWith("en") ? "ES" : "EN"}
          </button>

          {user && <NotificationBell />}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-stone-100"
                  data-testid="user-menu-trigger"
                >
                  <span className="w-8 h-8 rounded-full bg-[#1E3A8A] text-[#FACC15] grid place-items-center text-xs font-bold">
                    {initials}
                  </span>
                  <span className="hidden sm:flex items-center gap-1 text-sm font-medium">
                    {user.name?.split(" ")[0]}
                    <ChevronDown size={14} />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{user.email}</div>
                  <div className="text-xs text-[#1E3A8A] font-semibold mt-1">{t(`roles.${user.role}`)}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="text-red-600">
                  <LogOut size={14} className="mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-md hover:bg-stone-100"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div onClick={() => setMobileOpen(false)} className="absolute inset-0 bg-black/40" />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-5 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <span className="font-display text-lg">{t("app_name")}</span>
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-md hover:bg-stone-100">
                <X size={20} />
              </button>
            </div>
            <nav className="space-y-1">
              {visibleNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-[#E5EBFA] text-[#1E3A8A]" : "text-stone-700 hover:bg-stone-100"
                    }`
                  }
                  data-testid={`mobile-nav-${n.key}`}
                >
                  <n.icon size={16} /> {t(`nav.${n.key}`)}
                </NavLink>
              ))}
            </nav>
            {isAuditor && user?.assigned_store_id && (
              <div className="mt-4 flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-stone-50">
                <span className="text-sm">{t("common.view_all_stores")}</span>
                <Switch checked={viewAllStores} onCheckedChange={setViewAllStores} />
              </div>
            )}
            <button
              onClick={shareApp}
              className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-stone-100"
            >
              <Share2 size={14} /> {t("common.share_app")}
            </button>
            <button
              onClick={toggleLang}
              className="mt-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-stone-100"
            >
              <Languages size={14} /> {i18n.language?.startsWith("en") ? "Español" : "English"}
            </button>
            <div className="mt-auto pt-4 border-t border-stone-200">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut size={16} /> {t("common.logout")}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <AiHelpAssistant />

      <footer className="border-t border-stone-200 py-5 px-6 text-xs text-stone-500 flex items-center justify-between">
        <span>© {t("brand")} · {t("app_name")}</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={resetCookieConsent}
            className="underline hover:text-stone-800"
            data-testid="layout-cookie-preferences"
          >
            {t("cookies.manage")}
          </button>
          <span className="hidden md:inline">v2.0</span>
        </div>
      </footer>
    </div>
  );
}
